import {
  Bot, BrainCircuit, BriefcaseBusiness, ChartNoAxesCombined, Code2,
  GraduationCap, Lightbulb, MessagesSquare, Microscope, Network,
  Presentation, Puzzle, Rocket, School, Sparkles, UsersRound,
} from 'lucide-react'

export const heroSlides = [
  {
    eyebrow: 'AI learning that moves beyond theory',
    title: 'Build the confidence to create with AI.',
    text: 'Immersive technology training for college students, professors and corporate teams—designed around real tools, guided practice and measurable outcomes.',
    primary: 'Explore programs',
    primaryTo: '/programs',
    secondary: 'How we teach',
    secondaryTo: '/methodology',
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=1600&q=88',
    alt: 'A digital interpretation of artificial intelligence',
  },
  {
    eyebrow: 'For colleges and universities',
    title: 'Turn curious learners into capable builders.',
    text: 'Workshops, labs and faculty enablement that make modern AI concepts accessible, useful and exciting across disciplines.',
    primary: 'Student learning',
    primaryTo: '/programs',
    secondary: 'Meet Qubispark',
    secondaryTo: '/about',
    image: 'https://images.unsplash.com/photo-1523580846011-d3a5bc25702b?auto=format&fit=crop&w=1600&q=88',
    alt: 'University students celebrating together',
  },
  {
    eyebrow: 'Built for the way your team works',
    title: 'Make AI useful across your organization.',
    text: 'Role-based corporate training that connects AI literacy, workflow improvement and hands-on experimentation to your business priorities.',
    primary: 'Corporate training',
    primaryTo: '/corporate-training',
    secondary: 'Start a conversation',
    secondaryTo: '/contact',
    image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1600&q=88',
    alt: 'A professional team collaborating around a laptop',
  },
]

export const audiences = [
  { icon: GraduationCap, title: 'College students', text: 'Build practical fluency through guided labs, projects and portfolio-ready outcomes.' },
  { icon: Presentation, title: 'Professors & educators', text: 'Understand emerging tools, modernize teaching practice and confidently guide learners.' },
  { icon: BriefcaseBusiness, title: 'Corporate teams', text: 'Apply AI to real workflows with training tailored to roles, goals and organizational context.' },
  { icon: School, title: 'Institutions', text: 'Launch scalable learning initiatives with curriculum support, faculty enablement and labs.' },
]

export const capabilities = [
  { icon: Bot, title: 'AI tools & productivity', text: 'Prompting, research, content workflows, automation and tool evaluation.' },
  { icon: BrainCircuit, title: 'Generative AI foundations', text: 'Core concepts, models, capabilities, constraints and practical patterns.' },
  { icon: Code2, title: 'Applied AI development', text: 'APIs, retrieval, agents, prototyping and deployment fundamentals.' },
  { icon: Microscope, title: 'AI labs for students', text: 'Facilitated experimentation where learners test, build, reflect and demonstrate.' },
  { icon: ChartNoAxesCombined, title: 'Data & decision skills', text: 'Translate data into useful analysis, visualization and informed action.' },
  { icon: Network, title: 'Automation & agents', text: 'Design multi-step workflows that connect tools, knowledge and human review.' },
]

export const learningLevels = [
  {
    number: '01', label: 'Starter', title: 'Discover and practise',
    text: 'For learners new to AI tools. Develop vocabulary, understand what modern systems can do and complete structured exercises with immediate feedback.',
    outcomes: ['AI foundations and essential terminology', 'Effective prompting patterns', 'Everyday research and productivity workflows', 'Guided mini-labs'],
  },
  {
    number: '02', label: 'Medium', title: 'Apply and connect',
    text: 'For learners ready to solve broader problems. Compare tools, design repeatable workflows and turn concepts into useful prototypes.',
    outcomes: ['Structured prompt systems', 'Multimodal content workflows', 'Data analysis and automation', 'Team-based applied project'],
  },
  {
    number: '03', label: 'Advanced', title: 'Build and lead',
    text: 'For technical learners and champions. Work with models, APIs and AI architectures while making sound implementation choices.',
    outcomes: ['LLM APIs and application patterns', 'Retrieval and knowledge systems', 'Agentic workflows and evaluation', 'Capstone implementation'],
  },
]

export const methodSteps = [
  { icon: MessagesSquare, number: '01', title: 'Discover', text: 'We align the audience, current capability, learning context and outcomes before designing the experience.' },
  { icon: Puzzle, number: '02', title: 'Design', text: 'We shape a relevant pathway with examples, tools and challenges that fit the learners—not a generic slide deck.' },
  { icon: Lightbulb, number: '03', title: 'Demonstrate', text: 'Facilitators make complex ideas visible through clear explanations, live builds and practical decision-making.' },
  { icon: Microscope, number: '04', title: 'Do', text: 'Learners practise in structured labs, receive feedback and improve their work through focused iteration.' },
  { icon: Rocket, number: '05', title: 'Deliver', text: 'Every pathway culminates in a demonstration, applied project or workflow learners can carry forward.' },
  { icon: ChartNoAxesCombined, number: '06', title: 'Develop', text: 'We review evidence of progress and recommend the next skills, practice and support needed for continued growth.' },
]

export const values = [
  { icon: Sparkles, title: 'Clarity first', text: 'We make sophisticated technology understandable without oversimplifying it.' },
  { icon: Code2, title: 'Practice over passive learning', text: 'Learners build capability by working through meaningful problems.' },
  { icon: UsersRound, title: 'Human guidance matters', text: 'Expert facilitation, feedback and reflection turn tool use into lasting skill.' },
]
